import { useMemo } from "react";
import { Modal } from "@/components/ui/Modal";
import type { ContratoDto, LocacaoDto, MaquinarioDto, PeriodRange, RegistroHorimetroDto } from "@/services/locacoes";
import { isContratoPorHoras } from "@/services/locacoes";
import { formatCurrency, formatDate } from "@/utils/format";
import { formatHoras, formatPeriodLabel, groupRegistrosPorDia, locacoesTableHeadClass } from "./locacoesUtils";

const WEEKDAY = ["domingo", "segunda", "terça", "quarta", "quinta", "sexta", "sábado"] as const;

interface Props {
  open: boolean;
  onClose: () => void;
  period: PeriodRange;
  totalHoras: number;
  registros: RegistroHorimetroDto[];
  contratos: ContratoDto[];
  locacoes: LocacaoDto[];
  maquinarios: MaquinarioDto[];
  maquinaId?: string;
}

function clockLabel(iso: string | undefined): string {
  if (!iso) return "—";
  const match = iso.match(/(\d{2}):(\d{2})/);
  return match ? `${match[1]}:${match[2]}` : "—";
}

function weekdayLabel(isoDate: string): string {
  const date = new Date(`${isoDate}T12:00:00`);
  return WEEKDAY[date.getDay()] ?? "";
}

function daysInPeriod(period: PeriodRange): number {
  const start = new Date(`${period.startDate}T12:00:00`);
  const end = new Date(`${period.endDate}T12:00:00`);
  return Math.max(1, Math.round((end.getTime() - start.getTime()) / 86400000) + 1);
}

function locacaoNoDia(
  locacoes: LocacaoDto[],
  maquinaId: string,
  isoDate: string,
  contratoId?: string,
): LocacaoDto | undefined {
  const daMaquina = locacoes.filter((l) => l.maquinaId === maquinaId);
  const covering = daMaquina.filter((l) => {
    if (contratoId && l.contratoId && l.contratoId !== contratoId) return false;
    const start = l.dataInicio.slice(0, 10);
    const end = (l.dataFimReal || l.dataFimPrevista || "9999-12-31").slice(0, 10);
    return isoDate >= start && isoDate <= end;
  });
  const pool = covering.length > 0 ? covering : daMaquina.filter((l) => !contratoId || l.contratoId === contratoId);
  return [...pool].sort((a, b) => b.dataInicio.localeCompare(a.dataInicio))[0];
}

function valorHoraContrato(contrato: ContratoDto | undefined, maquinaId: string): number {
  if (!contrato || !isContratoPorHoras(contrato.modeloContrato)) return 0;
  const porMaquina = contrato.valorHoraPorMaquina?.[maquinaId];
  if (porMaquina != null && porMaquina > 0) return porMaquina;
  return contrato.valorHora > 0 ? contrato.valorHora : 0;
}

export function HorasPeriodoModal({
  open,
  onClose,
  period,
  totalHoras,
  registros,
  contratos,
  locacoes,
  maquinarios,
  maquinaId,
}: Props) {
  const detalhe = useMemo(() => {
    const regs = maquinaId ? registros.filter((r) => r.maquinaId === maquinaId) : registros;
    const agrupados = groupRegistrosPorDia(regs);
    const sessoes = agrupados
      .filter(
        (row) =>
          !row.operacaoAberta &&
          row.horasTrabalhadas != null &&
          row.horasTrabalhadas > 0 &&
          row.data >= period.startDate &&
          row.data <= period.endDate,
      )
      .sort((a, b) => b.data.localeCompare(a.data) || (b.final?.dataHora ?? "").localeCompare(a.final?.dataHora ?? ""));
    const abertas = agrupados.filter((row) => row.operacaoAberta).length;

    const linhas = sessoes.map((row) => {
      const contratoId = row.final?.contratoId || row.inicio?.contratoId;
      const contrato = contratoId ? contratos.find((c) => c.id === contratoId) : undefined;
      const locacao = locacaoNoDia(locacoes, row.maquinaId, row.data, contratoId);
      const maquina = maquinarios.find((m) => m.id === row.maquinaId);
      const taxa = valorHoraContrato(contrato, row.maquinaId);
      const horas = row.horasTrabalhadas ?? 0;
      const obra = (locacao?.obraNome || locacao?.costCenterLabel || contrato?.costCenterLabel || "").trim();
      return {
        id: row.id,
        data: row.data,
        maquinaId: row.maquinaId,
        maquinaNome: maquina?.nome || row.maquinaNome,
        contratoId: contrato?.id ?? "",
        contratoCodigo: contrato?.codigoContrato || "Sem contrato",
        cliente: contrato?.clienteSiengeNome?.trim() || "Sem cliente",
        obra: obra || "—",
        operador: row.final?.usuarioNome || row.inicio?.usuarioNome || "—",
        inicioHora: clockLabel(row.inicio?.dataHora),
        finalHora: clockLabel(row.final?.dataHora),
        horimetroInicio: row.inicio?.horimetroInformado,
        horimetroFinal: row.final?.horimetroInformado,
        horas,
        valor: taxa > 0 ? Math.round(horas * taxa * 100) / 100 : 0,
      };
    });

    const soma = linhas.reduce((s, row) => s + row.horas, 0);
    const valor = linhas.reduce((s, row) => s + row.valor, 0);
    const dias = new Set(linhas.map((row) => row.data));
    const operadores = new Set(linhas.map((row) => row.operador).filter((nome) => nome !== "—"));

    const porDia = [...dias]
      .sort((a, b) => b.localeCompare(a))
      .map((data) => {
        const doDia = linhas.filter((row) => row.data === data);
        return {
          data,
          horas: doDia.reduce((s, row) => s + row.horas, 0),
          apontamentos: doDia.length,
          maquinas: new Set(doDia.map((row) => row.maquinaId)).size,
        };
      });

    const porContratoMap = new Map<string, { codigo: string; cliente: string; horas: number; valor: number; apontamentos: number }>();
    for (const row of linhas) {
      const key = row.contratoId || "sem-contrato";
      const current = porContratoMap.get(key) ?? {
        codigo: row.contratoCodigo,
        cliente: row.cliente,
        horas: 0,
        valor: 0,
        apontamentos: 0,
      };
      current.horas += row.horas;
      current.valor += row.valor;
      current.apontamentos += 1;
      porContratoMap.set(key, current);
    }
    const porContrato = [...porContratoMap.values()].sort((a, b) => b.horas - a.horas);

    const porClienteMap = new Map<string, { cliente: string; horas: number; contratos: number }>();
    for (const row of porContrato) {
      const current = porClienteMap.get(row.cliente) ?? { cliente: row.cliente, horas: 0, contratos: 0 };
      current.horas += row.horas;
      current.contratos += 1;
      porClienteMap.set(row.cliente, current);
    }
    const porCliente = [...porClienteMap.values()].sort((a, b) => b.horas - a.horas);

    const porMaquinaMap = new Map<string, { nome: string; horas: number; dias: Set<string> }>();
    for (const row of linhas) {
      const current = porMaquinaMap.get(row.maquinaId) ?? { nome: row.maquinaNome, horas: 0, dias: new Set<string>() };
      current.horas += row.horas;
      current.dias.add(row.data);
      porMaquinaMap.set(row.maquinaId, current);
    }
    const porMaquina = [...porMaquinaMap.values()]
      .map((row) => ({ nome: row.nome, horas: row.horas, dias: row.dias.size }))
      .sort((a, b) => b.horas - a.horas);

    return {
      linhas,
      soma,
      valor,
      diasTrabalhados: dias.size,
      diasPeriodo: daysInPeriod(period),
      operadores: operadores.size,
      abertas,
      porDia,
      porContrato,
      porCliente,
      porMaquina,
    };
  }, [contratos, locacoes, maquinaId, maquinarios, period.endDate, period.startDate, registros]);

  const maquinaNome = maquinaId ? maquinarios.find((m) => m.id === maquinaId)?.nome : undefined;
  const mediaDia = detalhe.diasTrabalhados > 0 ? detalhe.soma / detalhe.diasTrabalhados : 0;
  const share = (horas: number) => (detalhe.soma > 0 ? Math.round((horas / detalhe.soma) * 1000) / 10 : 0);

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="xl"
      closeOnEscape
      closeOnBackdropClick
      title="Detalhe das horas"
    >
      <div className="flex flex-col gap-5">
        <div>
          <p className="text-2xl font-bold tabular-nums text-slate-800">{formatHoras(totalHoras)}</p>
          <p className="mt-1 text-sm text-slate-500">
            {formatPeriodLabel(period)}
            {maquinaNome ? ` · ${maquinaNome}` : ""}
          </p>
          {Math.abs(detalhe.soma - totalHoras) > 0.05 ? (
            <p className="mt-1 text-xs text-slate-400">
              Soma dos apontamentos abaixo: {formatHoras(detalhe.soma)}
            </p>
          ) : null}
        </div>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <ResumoChip label="Apontamentos" value={String(detalhe.linhas.length)} />
          <ResumoChip
            label="Dias com hora"
            value={`${detalhe.diasTrabalhados} de ${detalhe.diasPeriodo}`}
          />
          <ResumoChip label="Média por dia trabalhado" value={formatHoras(mediaDia)} />
          <ResumoChip label="Operadores" value={String(detalhe.operadores)} />
        </div>

        {detalhe.valor > 0 ? (
          <p className="rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
            Valor estimado pelos contratos de horímetro: <strong>{formatCurrency(detalhe.valor)}</strong>
          </p>
        ) : null}

        {detalhe.abertas > 0 ? (
          <p className="rounded-lg border border-amber-100 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            {detalhe.abertas === 1
              ? "Há 1 horímetro em aberto. Ele só entra no total quando o final for registrado."
              : `Há ${detalhe.abertas} horímetros em aberto. Eles só entram no total quando o final for registrado.`}
          </p>
        ) : null}

        {detalhe.linhas.length === 0 ? (
          <p className="py-8 text-center text-sm text-slate-500">Nenhuma hora apontada neste período.</p>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
              <section>
                <h3 className="mb-2 text-sm font-semibold text-slate-800">Por cliente</h3>
                <div className="overflow-x-auto rounded-xl border border-slate-200">
                  <table className="min-w-full text-sm">
                    <thead className={locacoesTableHeadClass}>
                      <tr>
                        <th className="px-3 py-2 text-left">Cliente</th>
                        <th className="px-3 py-2 text-right">Contratos</th>
                        <th className="px-3 py-2 text-right">Horas</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {detalhe.porCliente.map((row) => (
                        <tr key={row.cliente}>
                          <td className="px-3 py-2 text-slate-700">{row.cliente}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-slate-600">{row.contratos}</td>
                          <td className="px-3 py-2 text-right font-medium tabular-nums text-slate-800">
                            {formatHoras(row.horas)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              <section>
                <h3 className="mb-2 text-sm font-semibold text-slate-800">Por contrato</h3>
                <div className="overflow-x-auto rounded-xl border border-slate-200">
                  <table className="min-w-full text-sm">
                    <thead className={locacoesTableHeadClass}>
                      <tr>
                        <th className="px-3 py-2 text-left">Contrato</th>
                        <th className="px-3 py-2 text-right">Horas</th>
                        <th className="px-3 py-2 text-right">Parte</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {detalhe.porContrato.map((row) => (
                        <tr key={`${row.codigo}-${row.cliente}`}>
                          <td className="px-3 py-2">
                            <p className="font-medium text-slate-800">{row.codigo}</p>
                            <p className="text-xs text-slate-500">{row.cliente}</p>
                          </td>
                          <td className="px-3 py-2 text-right font-medium tabular-nums text-slate-800">
                            {formatHoras(row.horas)}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-slate-500">{share(row.horas)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            </div>

            <section>
              <h3 className="mb-2 text-sm font-semibold text-slate-800">Por dia</h3>
              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="min-w-full text-sm">
                  <thead className={locacoesTableHeadClass}>
                    <tr>
                      <th className="px-3 py-2 text-left">Dia</th>
                      <th className="px-3 py-2 text-right">Apontamentos</th>
                      {!maquinaId ? <th className="px-3 py-2 text-right">Máquinas</th> : null}
                      <th className="px-3 py-2 text-right">Horas</th>
                      <th className="px-3 py-2 text-right">Parte</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {detalhe.porDia.map((row) => (
                      <tr key={row.data}>
                        <td className="px-3 py-2 text-slate-700">
                          <span className="capitalize">{weekdayLabel(row.data)}</span>
                          <span className="text-slate-400"> · {formatDate(row.data)}</span>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-600">{row.apontamentos}</td>
                        {!maquinaId ? (
                          <td className="px-3 py-2 text-right tabular-nums text-slate-600">{row.maquinas}</td>
                        ) : null}
                        <td className="px-3 py-2 text-right font-medium tabular-nums text-slate-800">
                          {formatHoras(row.horas)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-500">{share(row.horas)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {detalhe.porMaquina.length > 1 ? (
              <section>
                <h3 className="mb-2 text-sm font-semibold text-slate-800">Por máquina</h3>
                <div className="overflow-x-auto rounded-xl border border-slate-200">
                  <table className="min-w-full text-sm">
                    <thead className={locacoesTableHeadClass}>
                      <tr>
                        <th className="px-3 py-2 text-left">Máquina</th>
                        <th className="px-3 py-2 text-right">Dias</th>
                        <th className="px-3 py-2 text-right">Horas</th>
                        <th className="px-3 py-2 text-right">Parte</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {detalhe.porMaquina.map((row) => (
                        <tr key={row.nome}>
                          <td className="px-3 py-2 text-slate-700">{row.nome}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-slate-600">{row.dias}</td>
                          <td className="px-3 py-2 text-right font-medium tabular-nums text-slate-800">
                            {formatHoras(row.horas)}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-slate-500">{share(row.horas)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            ) : null}

            <section>
              <h3 className="mb-2 text-sm font-semibold text-slate-800">Apontamentos</h3>
              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="min-w-full text-sm">
                  <thead className={locacoesTableHeadClass}>
                    <tr>
                      <th className="px-3 py-2 text-left">Dia</th>
                      <th className="px-3 py-2 text-left">Máquina</th>
                      <th className="px-3 py-2 text-left">Cliente / contrato</th>
                      <th className="px-3 py-2 text-left">Obra</th>
                      <th className="px-3 py-2 text-left">Operador</th>
                      <th className="px-3 py-2 text-right">Horímetro</th>
                      <th className="px-3 py-2 text-right">Horas</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {detalhe.linhas.map((row) => (
                      <tr key={row.id}>
                        <td className="whitespace-nowrap px-3 py-2 text-slate-700">
                          <p>{formatDate(row.data)}</p>
                          <p className="text-xs text-slate-400">
                            {row.inicioHora} – {row.finalHora}
                          </p>
                        </td>
                        <td className="px-3 py-2 text-slate-700">{row.maquinaNome}</td>
                        <td className="px-3 py-2">
                          <p className="text-slate-800">{row.cliente}</p>
                          <p className="text-xs text-slate-500">{row.contratoCodigo}</p>
                        </td>
                        <td className="max-w-[12rem] px-3 py-2 text-slate-600">{row.obra}</td>
                        <td className="px-3 py-2 text-slate-600">{row.operador}</td>
                        <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-slate-600">
                          {row.horimetroInicio != null ? row.horimetroInicio : "—"}
                          {" → "}
                          {row.horimetroFinal != null ? row.horimetroFinal : "—"}
                        </td>
                        <td className="px-3 py-2 text-right font-medium tabular-nums text-slate-800">
                          {formatHoras(row.horas)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
      </div>
    </Modal>
  );
}

function ResumoChip({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-0.5 text-sm font-semibold tabular-nums text-slate-800">{value}</p>
    </div>
  );
}
